import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

export default function Terms() {
  const [terms, setTerms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = () => ({ name: "", content: "", is_default: false });
  const [form, setForm] = useState(empty());

  const load = async () => setTerms((await api.get("/terms")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/terms/${editing.term_id}`, form);
      else await api.post("/terms", form);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); setEditing(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this terms template?")) return;
    await api.delete(`/terms/${id}`); toast.success("Deleted"); load();
  };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty(), ...t }); setOpen(true); };

  return (
    <div>
      <PageHeader
        title="Terms Library"
        subtitle="Save reusable comments / special instructions for POs"
        action={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-term-btn">
                <Plus className="w-4 h-4 mr-1" /> New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rubber Goods" data-testid="term-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Instructions</Label>
                  <Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} />
                  <span className="text-sm text-slate-600">Use as default when creating a new PO</span>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} data-testid="save-term-btn">
                  {editing ? "Save changes" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {terms.length === 0 && (
          <Card className="p-8 text-center text-slate-500 border-slate-200 md:col-span-2">
            No templates yet. Create one to speed up your POs.
          </Card>
        )}
        {terms.map((t) => (
          <Card key={t.term_id} className="p-5 border-slate-200 card-hover flex flex-col">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-display font-bold text-lg">{t.name}</h3>
                {t.is_default && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] uppercase tracking-wider">
                    <Star className="w-3 h-3 mr-1" /> Default
                  </Badge>
                )}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(t)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => del(t.term_id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </div>
            </div>
            <pre className="mt-3 text-sm text-slate-600 whitespace-pre-wrap font-sans">{t.content}</pre>
          </Card>
        ))}
      </div>
    </div>
  );
}
